import { getChromaticScale, getRootNote, isMinorKey, getNoteAtInterval } from './diatonicChords';
import { getChordNotes } from './chordNotes';

/**
 * Get the scale notes for the current key (only once across two octaves)
 */
export const getScaleNotes = (key) => {
  if (!key || typeof key !== 'string') return [];

  try {
    const rootNote = getRootNote(key);
    const isMinor = isMinorKey(key);
    const scale = getChromaticScale(key);

    // Major scale intervals: 0, 2, 4, 5, 7, 9, 11
    // Minor scale intervals: 0, 2, 3, 5, 7, 8, 10
    const intervals = isMinor
      ? [0, 2, 3, 5, 7, 8, 10] // Natural minor scale
      : [0, 2, 4, 5, 7, 9, 11]; // Major scale

    const scaleNotes = [];

    for (let i = 0; i < 7; i++) {
      const note = getNoteAtInterval(rootNote, intervals[i], scale);
      if (note) {
        scaleNotes.push(note);
      }
    }

    return scaleNotes;
  } catch (error) {
    return [];
  }
};

/**
 * Check if a key should be highlighted based on scale position
 */
export const shouldHighlightKey = (key, scaleNotes, allKeys, currentKey) => {
  const normalizedNote = normalizeNote(key.note);
  const scaleIndex = scaleNotes.findIndex((note) => normalizeNote(note) === normalizedNote);

  if (scaleIndex === -1) return false;

  // Find the first occurrence of the root note in the keyboard
  const rootNote = scaleNotes[0];
  const normalizedRoot = normalizeNote(rootNote);
  const firstRootIndex = allKeys.findIndex((k) => normalizeNote(k.note) === normalizedRoot);

  if (firstRootIndex === -1) return false;

  // Calculate the expected position for this scale degree
  const currentKeyIndex = allKeys.findIndex((k) => k.id === key.id);
  const expectedInterval = isMinorKey(currentKey)
    ? [0, 2, 3, 5, 7, 8, 10][scaleIndex]
    : [0, 2, 4, 5, 7, 9, 11][scaleIndex];

  const expectedIndex = firstRootIndex + expectedInterval;

  // Highlight if this is the expected position OR if it's the octave completion (root note again)
  if (currentKeyIndex === expectedIndex) return true;

  // Also highlight the octave completion (root note one octave higher)
  if (scaleIndex === 0 && currentKeyIndex === firstRootIndex + 12) return true;

  return false;
};

/**
 * Check if a note is a black key
 */
export const isBlackKey = (note) => {
  return note.includes('#') || note.includes('b');
};

/**
 * Normalize note names to handle enharmonic equivalents
 */
export const normalizeNote = (note) => {
  const enharmonicMap = {
    'C#': 'Db',
    'D#': 'Eb',
    'F#': 'Gb',
    'G#': 'Ab',
    'A#': 'Bb',
  };

  return enharmonicMap[note] || note;
};

/**
 * Generate two octaves of piano keys
 */
export const generatePianoKeys = (currentKey) => {
  const scale = getChromaticScale(currentKey || 'C');
  const keys = [];

  // Generate two octaves
  for (let octave = 0; octave < 2; octave++) {
    for (let i = 0; i < 12; i++) {
      const note = scale[i];
      const keyId = `${note}-${octave}`;
      keys.push({
        id: keyId,
        note: note,
        isBlack: isBlackKey(note),
        octave: octave,
      });
    }
  }

  return keys;
};

/**
 * Generate piano keys for a specific chord, starting from the root note.
 * Produces exactly Math.max(12, maxInterval) + 1 keys so that all chord tones
 * fit within one octave, extended only as far as needed (e.g. add9 = 15 keys).
 *
 * When a bassNote is supplied (slash chords), a single additional key is
 * prepended with { isBass: true } and semitoneOffset: null, separated from
 * the main keyboard visually by the consuming component.
 *
 * @param {string} rootNote - The chord root (e.g. "A", "F#", "Bb")
 * @param {number} maxInterval - Highest semitone interval in the chord
 * @param {string|null} bassNote - Optional bass note for slash chords
 * @returns {Array<{id, note, isBlack, semitoneOffset, isBass}>}
 */
export const generateChordPianoKeys = (rootNote, maxInterval, bassNote = null) => {
  const scale = getChromaticScale(rootNote);
  const rootIdx = scale.indexOf(rootNote);

  if (rootIdx === -1) return [];

  const totalKeys = Math.max(12, maxInterval) + 1;
  const keys = [];

  for (let i = 0; i < totalKeys; i++) {
    const noteIdx = (rootIdx + i) % 12;
    const note = scale[noteIdx];
    keys.push({
      id: `chord-key-${note}-${i}`,
      note,
      isBlack: isBlackKey(note),
      semitoneOffset: i,
      isBass: false,
    });
  }

  // Pad with white keys at edges so no black key hangs without a neighbour.
  // Adjacent notes in the chromatic scale are never both black, so one step
  // is always enough to reach a white key.
  if (keys.length > 0 && keys[0].isBlack) {
    const prevNoteIdx = (rootIdx - 1 + 12) % 12;
    const prevNote = scale[prevNoteIdx];
    keys.unshift({
      id: `chord-key-${prevNote}-pre`,
      note: prevNote,
      isBlack: isBlackKey(prevNote),
      semitoneOffset: -1,
      isBass: false,
    });
  }

  if (keys.length > 0 && keys[keys.length - 1].isBlack) {
    const lastOffset = keys[keys.length - 1].semitoneOffset;
    const nextOffset = lastOffset + 1;
    const nextNoteIdx = (rootIdx + nextOffset) % 12;
    const nextNote = scale[nextNoteIdx];
    keys.push({
      id: `chord-key-${nextNote}-post`,
      note: nextNote,
      isBlack: isBlackKey(nextNote),
      semitoneOffset: nextOffset,
      isBass: false,
    });
  }

  if (bassNote) {
    keys.unshift({
      id: `chord-bass-${bassNote}`,
      note: bassNote,
      isBlack: isBlackKey(bassNote),
      semitoneOffset: null,
      isBass: true,
    });
  }

  return keys;
};

/**
 * Work out which rendered keys make up a chord, as indices into `allKeys`.
 *
 * Matching by note name alone lights every octave of every chord tone, so a chord
 * reads as whichever of its notes happens to fall earliest on the keyboard — Bm in
 * the key of Em lit F# first and looked like an F# chord. Anchoring on the root and
 * stacking the intervals upward keeps each chord in root position, and starting the
 * search at the tonic means the chords walk up the keyboard as you go i → VII.
 *
 * @param {string} currentKey - The song key (e.g. 'Em')
 * @param {string} chordName - The chord to locate (e.g. 'Bm')
 * @param {Array} allKeys - Keys from generatePianoKeys
 * @returns {Set<number>|null} - Indices of the chord's keys, or null if not locatable
 */
export const getChordKeyIndices = (currentKey, chordName, allKeys) => {
  if (!chordName || !allKeys?.length) return null;

  const { root, intervals } = getChordNotes(chordName);
  const normalizedRoot = normalizeNote(root);
  const maxInterval = Math.max(...intervals);

  // Enharmonic-safe: the chord root may be spelled differently from the keyboard
  // (a D chord resolves to sharps even inside the flat-spelled key of F).
  const tonicIndex = allKeys.findIndex((k) => normalizeNote(k.note) === normalizeNote(getRootNote(currentKey)));
  const searchFrom = Math.max(tonicIndex, 0);

  const rootPositions = allKeys.reduce(
    (found, k, i) => (normalizeNote(k.note) === normalizedRoot ? [...found, i] : found),
    [],
  );
  // The keyboard is a fixed two octaves, so the octave at or above the tonic does not
  // always have room for the whole chord — the vii° of a high key would lose its top
  // notes off the end. Fall back to a lower octave rather than truncate the chord.
  const fits = (i) => i + maxInterval < allKeys.length;
  const rootIndex =
    rootPositions.find((i) => i >= searchFrom && fits(i)) ?? [...rootPositions].reverse().find(fits) ?? rootPositions[0];

  if (rootIndex === undefined) return null;

  return new Set(intervals.map((semitones) => rootIndex + semitones).filter((i) => i < allKeys.length));
};
